import { logoDesktop } from '@frontend/assets';
import { Box, LinearProgress, styled } from '@mui/material';

import { FeatureFlagsResponseDto } from '@bandido/shared/dtos';

import { createContext, useContext, useEffect, useState } from 'react';
import { useGetFeatureFlags } from './queries';

export type SiteConfigContextState = {
  settings?: FeatureFlagsResponseDto;
};

const Holder = styled('div')(
  ({ theme }) => `
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100vw;
  min-height: 100vh;
  background-color: ${theme.palette.primary.main};
  gap: ${theme.spacing(4)};
  transition: 1s;
  opacity: 1;
  &.fadeOut {
    opacity: 0;
  }
`,
);

export const SiteConfigContext = createContext<SiteConfigContextState>({} as SiteConfigContextState);

export const useSiteConfig = () => {
  const context = useContext(SiteConfigContext);
  return { ...context };
};

interface Props {
  children: JSX.Element;
}

export const SiteConfigContextProvider = ({ children }: Props) => {
  const [showLoaderScreen, setShowLoaderScreen] = useState<boolean>(true);
  const [progress, setProgress] = useState(0);
  const { data, isLoading } = useGetFeatureFlags();

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (isLoading) {
          const diff = Math.random() * 10;
          return Math.min(oldProgress + diff, 90);
        } else {
          clearInterval(timer);
        }
        return oldProgress;
      });
    }, 100);

    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setShowLoaderScreen(false);
    }
  }, [isLoading]);

  return (
    <SiteConfigContext.Provider value={{ settings: data?.data }}>
      {!isLoading && data && children}
      {showLoaderScreen && (
        <Holder className={!isLoading ? 'fadeOut' : ''}>
          <img width={330} src={logoDesktop} alt="me vuelo" />
          <Box width="100%">
            <LinearProgress color="secondary" variant="determinate" value={progress} />
          </Box>
        </Holder>
      )}
    </SiteConfigContext.Provider>
  );
};
